<script setup lang="ts">
import { ArrowUpRight, KeyRound, RefreshCw, RotateCcw, TriangleAlert } from '@lucide/vue'
import {
    Button,
    ZhEmptyState,
    ZhKeyValue,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusDot,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { api, formatAgo, type WebhookSecretStatus, type WebhookSummary } from '@/api'
import CopyButton from '@/components/CopyButton.vue'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import DocsLink from '@/components/DocsLink.vue'
import WebhookStatus from '@/components/WebhookStatus.vue'
import { useListState } from '@/composables/useListState'
import { useLiveResource } from '@/composables/useLiveResource'

const router = useRouter()
const page = ref(1)
const limit = ref(25)
const search = ref('')
const sort = ref('receivedAt')
const order = ref<'ASC' | 'DESC'>('DESC')
const filters = ref<Record<string, string>>({})
/** #633: the search, sort and filters come back on the next visit, in place before the first read. */
const list = useListState('webhooks', {
    search: { ref: search },
    sort: { ref: sort },
    order: { ref: order },
    filters: { ref: filters },
})
watch([search, sort, order, limit, filters], () => {
    page.value = 1
})
const query = computed(() => ({
    page: page.value,
    limit: limit.value,
    search: search.value,
    sort: sort.value,
    order: order.value,
    ...filters.value,
}))
const {
    data: history,
    error: historyError,
    loading,
    refreshing,
    now,
    visible,
    refresh,
} = useLiveResource(
    () => JSON.stringify(query.value),
    signal => api.webhooks(query.value, signal),
)
const columns: DataTableColumn[] = [
    { key: 'target', label: 'Webhook', sort: 'target', filterable: false, class: 'min-w-52' },
    {
        key: 'kind',
        label: 'Type',
        type: 'select',
        options: [
            { value: 'task', label: 'Task trigger' },
            { value: 'gate', label: 'Gate decision' },
        ],
    },
    {
        key: 'status',
        label: 'Status',
        sort: 'status',
        type: 'select',
        options: ['processing', 'accepted', 'skipped', 'rejected', 'failed'].map(value => ({
            value,
            label: value[0].toUpperCase() + value.slice(1),
        })),
    },
    { key: 'responseStatus', label: 'Response', sort: 'responseStatus', filterable: false },
    { key: 'executionId', label: 'Execution', filterable: false },
    { key: 'receivedAt', label: 'Received', sort: 'receivedAt', filterable: false, class: 'whitespace-nowrap' },
    { key: 'durationMs', label: 'Duration', sort: 'durationMs', filterable: false, class: 'whitespace-nowrap' },
    { key: 'id', label: 'Receipt ID', filterable: false, defaultVisible: false },
]
/**
 * No webhook has ever been received. The table is hidden in this state, so it is deliberately keyed off
 * an *unfiltered* empty history: a search or filter that finds nothing must keep the table on screen,
 * or there is no control left to clear it with.
 */
const firstWebhook = computed(
    () => history.value?.total === 0 && !search.value && !Object.values(filters.value).some(Boolean),
)
function receipt(row: Record<string, unknown>): WebhookSummary {
    return row as unknown as WebhookSummary
}
function open(row: Record<string, unknown>) {
    void router.push(`/webhook/${encodeURIComponent(String(row.id))}`)
}
function duration(row: Record<string, unknown>) {
    const item = receipt(row)
    const ms = item.durationMs ?? now.value - new Date(item.receivedAt).getTime()
    return ms < 1000 ? `${Math.max(0, Math.round(ms))} ms` : `${(Math.max(0, ms) / 1000).toFixed(2)} s`
}

/**
 * Settings → Webhooks. Each enterprise has its own webhook secret, stored by the API only as a hash. The portal
 * therefore never holds a secret except in the one response that rotates it, and shows it exactly then.
 */
const status = ref<WebhookSecretStatus | null>(null)
const error = ref<string | null>(null)
const confirming = ref(false)
const rotating = ref(false)
/** Only in memory, only on this visit: leaving the page is the last chance to copy it. */
const newSecret = ref<string | null>(null)

onMounted(async () => {
    try {
        status.value = await api.webhookSecret()
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Could not load the webhook secret.'
    }
})

async function rotate(): Promise<void> {
    rotating.value = true
    error.value = null
    try {
        const rotated = await api.rotateWebhookSecret()
        newSecret.value = rotated.secret
        status.value = { hint: rotated.hint, rotatedAt: rotated.rotatedAt }
        confirming.value = false
    } catch (err) {
        error.value = err instanceof Error ? err.message : 'Rotating the webhook secret failed.'
    } finally {
        rotating.value = false
    }
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString()
}
</script>

<template>
	<section class="w-full min-w-0 space-y-6 p-4 md:p-8">
        <header class="flex flex-wrap items-start justify-between gap-4">
            <div class="space-y-1">
                <ZhPageTitle>
                    Webhooks
                    <template #meta><DocsLink path="/docs/api/webhooks" /></template>
                </ZhPageTitle>
                <p class="text-muted-foreground max-w-2xl text-sm">Every received task trigger and gate decision, with its response and the execution it started or continued.</p>
            </div>
            <div class="flex items-center gap-3">
                <span class="text-muted-foreground flex items-center gap-2 text-xs"><ZhStatusDot :state="historyError ? 'active' : visible ? 'complete' : 'inactive'" :pulse="visible" />{{ historyError ? 'Reconnecting…' : visible ? 'Live · every second' : 'Updates paused' }}</span>
                <Button variant="outline" size="icon-sm" aria-label="Refresh webhooks" :disabled="refreshing" @click="refresh"><RefreshCw :size="15" /></Button>
            </div>
        </header>

        <ZhPanel v-if="historyError" as="div" tone="failed" role="alert" class="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>{{ history ? 'Updates interrupted. Showing the last received webhooks.' : 'Could not load webhook history.' }}</span>
            <Button variant="outline" size="sm" :disabled="refreshing" @click="refresh">Try again</Button>
        </ZhPanel>
        <!-- The kit empty state sets its copy in one paragraph, so the two lines below are blocks of inline text. -->
        <ZhEmptyState v-if="firstWebhook" label="No webhooks received yet">
            <span class="block">Call a task's webhook URL from its Triggers panel. New requests will appear here automatically.</span>
            <span class="text-muted-foreground mt-1 block text-xs">History begins when webhook logging is enabled; earlier calls are not backfilled.</span>
            <template #action>
                <Button as-child size="sm"><RouterLink to="/tasks">Go to Tasks</RouterLink></Button>
            </template>
        </ZhEmptyState>
        <DataTable v-if="!firstWebhook" v-model:page="page" v-model:limit="limit" v-model:search="search" v-model:sort="sort" v-model:order="order" v-model:filters="filters" :list-state="list" :columns="columns" :rows="(history?.data ?? []) as unknown as Record<string, unknown>[]" :total="history?.total ?? 0" :pages="history?.pages ?? 1" :loading="loading" storage-key="webhooks" @row-click="open">
            <template #cell-target="{ row }">
                <RouterLink :to="`/webhook/${row.id}`" class="hover:text-primary block font-medium" @click.stop>{{ receipt(row).target.name }}</RouterLink>
                <span class="text-muted-foreground mt-1 flex items-center gap-1 font-mono text-xs"><RotateCcw v-if="row.replayOfId" :size="11" /><span v-if="row.replayOfId" class="font-sans">Replay · </span>{{ String(row.id).slice(0, 8) }}</span>
            </template>
            <template #cell-kind="{ row }"><span class="text-muted-foreground whitespace-nowrap text-xs">{{ receipt(row).target.kind === 'task' ? 'Task trigger' : 'Gate decision' }}</span></template>
            <template #cell-status="{ row }"><WebhookStatus :status="receipt(row).status" /></template>
            <template #cell-responseStatus="{ value }"><span v-if="value !== null" class="font-mono text-xs">HTTP {{ value }}</span><span v-else class="text-muted-foreground text-xs">Pending</span></template>
            <template #cell-executionId="{ row }"><RouterLink v-if="row.executionId" :to="`/executions/${row.executionId}`" class="text-primary inline-flex items-center gap-1 whitespace-nowrap text-xs" @click.stop>View execution <ArrowUpRight :size="12" /></RouterLink><span v-else class="text-muted-foreground text-xs">—</span></template>
            <template #cell-receivedAt="{ value }"><ZhTooltip :text="formatDate(String(value))"><time :datetime="String(value)" class="text-muted-foreground text-xs tabular-nums">{{ formatAgo(Math.max(0, now - new Date(String(value)).getTime())) }}</time></ZhTooltip></template>
            <template #cell-durationMs="{ row }"><span class="text-muted-foreground text-xs tabular-nums">{{ duration(row) }}</span></template>
            <template #cell-id="{ value }"><span class="font-mono text-xs">{{ value }}</span></template>
        </DataTable>

		<p v-if="error" class="text-destructive text-sm" role="alert">{{ error }}</p>

		<ZhPanel v-if="status" as="article" class="space-y-4">
			<ZhSectionHeading class="items-center">
				<KeyRound class="size-4 shrink-0" aria-hidden="true" />
				Webhook secret
			</ZhSectionHeading>

            <p class="text-muted-foreground text-sm">External calls authenticate with your enterprise's secret in the <code class="font-mono">x-os-webhook-secret</code> header. Each task's webhook URL is on its Triggers panel.</p>
			<dl>
				<ZhKeyValue label="In use">
					<span data-testid="webhook-secret-hint">ending in <code>…{{ status.hint }}</code></span>
				</ZhKeyValue>
				<ZhKeyValue label="Last rotated">
					<span v-if="status.rotatedAt" data-testid="webhook-secret-rotated-at">{{ formatDate(status.rotatedAt) }}</span>
					<span v-else data-testid="webhook-secret-never-rotated">
						Never. The secret made with your enterprise has not been shown to anyone, so webhooks cannot be used until
						you rotate it.
					</span>
				</ZhKeyValue>
			</dl>

			<ZhPanel
				v-if="newSecret"
				as="div"
				tone="action"
				class="space-y-2 text-sm"
				data-testid="webhook-secret-new"
			>
				<p class="flex items-start gap-2 font-medium">
					<TriangleAlert class="mt-0.5 size-4 shrink-0 text-warning-amber" aria-hidden="true" />
					Copy this secret now. It won't be shown again.
				</p>
				<div class="flex flex-wrap items-center gap-2">
					<code class="min-w-0 flex-1 overflow-x-auto font-mono text-xs">{{ newSecret }}</code>
					<CopyButton :value="newSecret" label="webhook secret" />
				</div>
			</ZhPanel>

			<ZhPanel
				v-if="confirming"
				as="div"
				tone="failed"
				class="space-y-3 text-sm"
			>
				<p data-testid="webhook-secret-confirm-text">
					The current secret stops working immediately. Anything calling your webhooks gets <code class="font-mono">401</code>
					until it has the new one.
				</p>
				<div class="flex gap-2">
					<Button
						type="button"
						size="sm"
						variant="destructive"
						:disabled="rotating"
						data-testid="webhook-secret-confirm"
						@click="rotate"
					>
						Rotate now
					</Button>
					<Button
						type="button"
						size="sm"
						variant="outline"
						:disabled="rotating"
						data-testid="webhook-secret-cancel"
						@click="confirming = false"
					>
						Cancel
					</Button>
				</div>
			</ZhPanel>
			<Button
				v-else
				type="button"
				size="sm"
				variant="outline"
				data-testid="webhook-secret-rotate"
				@click="confirming = true"
			>
				Rotate secret
			</Button>
		</ZhPanel>
	</section>
</template>
